import { useEffect, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuthStore } from '../stores/authStore';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * AuthGuard component for protecting routes
 *
 * Wraps protected routes and handles:
 * - Redirect to login if not authenticated
 * - Session expiry validation
 * - Preserving intended destination for post-login redirect
 * - Loading state while checking auth
 *
 * @example
 * ```tsx
 * <Route element={<AuthGuard><AppLayout /></AuthGuard>}>
 *   <Route path="/app/dashboard" element={<Dashboard />} />
 * </Route>
 * ```
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, checkAuth, setRedirectPath } = useAuthStore();

  useEffect(() => {
    // Check if session is still valid
    const isValid = checkAuth();

    if (!isValid) {
      // Store the intended destination for redirect after login
      setRedirectPath(location.pathname + location.search);
      navigate('/login', { replace: true });
    }
  }, [checkAuth, navigate, location, setRedirectPath]);

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: 'var(--ant-color-bg-layout)',
        }}
      >
        <Spin size="large">
          <div style={{ padding: 50, textAlign: 'center' }}>Loading...</div>
        </Spin>
      </div>
    );
  }

  // Don't render children if not authenticated
  if (!isAuthenticated) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: 'var(--ant-color-bg-layout)',
        }}
      >
        <Spin size="large">
          <div style={{ padding: 50, textAlign: 'center' }}>Redirecting to login...</div>
        </Spin>
      </div>
    );
  }

  return <>{children}</>;
}

export default AuthGuard;
